import { create, all } from "mathjs";

const math = create(all, {});

export interface CompiledFn {
  expr: string;
  ok: boolean;
  error?: string;
  evaluate: (x: number) => number;
}

export interface SamplePoint {
  x: number;
  y: number;
  defined: boolean;
}

export interface FunctionRow {
  id: string;
  expr: string; // RHS only, e.g. "x^2"
  color: string;
  visible: boolean;
}

export interface Viewport {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export interface SpecialPoints {
  zeros: number[];          // x where y ≈ 0
  yIntercept: number | null; // y at x=0
  extrema: { x: number; y: number; kind: "min" | "max" }[];
}

/** Sanitize incoming expression — strip leading "y =" or "f(x) =" */
export function normalizeExpr(raw: string): string {
  let s = raw.trim();
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/^\s*f\s*\(\s*x\s*\)\s*=\s*/i, "");
  return s;
}

/** Compile a function string into a fast evaluator. */
export function compile(expr: string, params: Record<string, number> = {}): CompiledFn {
  const e = normalizeExpr(expr);
  if (!e) {
    return { expr, ok: false, error: "empty", evaluate: () => NaN };
  }
  try {
    const node = math.parse(e);
    const code = node.compile();
    return {
      expr,
      ok: true,
      evaluate: (x: number) => {
        try {
          const v = code.evaluate({ x, ...params });
          if (typeof v === "number" && Number.isFinite(v)) return v;
          if (typeof v === "object" && v !== null && "re" in v) return NaN; // complex
          return NaN;
        } catch {
          return NaN;
        }
      },
    };
  } catch (err) {
    return {
      expr,
      ok: false,
      error: err instanceof Error ? err.message : "parse error",
      evaluate: () => NaN,
    };
  }
}

export interface SampleOpts {
  xMin: number;
  xMax: number;
  resolution: number;
  trigMode?: "rad" | "deg";
}

/** Sample a function across xMin..xMax. Returns points; marks undefined where NaN. */
export function sample(fn: CompiledFn, opts: SampleOpts): SamplePoint[] {
  const { xMin, xMax, resolution, trigMode = "rad" } = opts;
  const n = Math.max(2, Math.min(5000, Math.floor(resolution)));
  const step = (xMax - xMin) / (n - 1);
  const pts: SamplePoint[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const x = xMin + step * i;
    const xEval = trigMode === "deg" ? (x * Math.PI) / 180 : x;
    const y = fn.evaluate(xEval);
    pts[i] = { x, y, defined: Number.isFinite(y) };
  }
  return pts;
}

/** Group points into continuous segments where y is defined and not jumping (asymptote heuristic). */
export function toSegments(points: SamplePoint[], yRange: number): SamplePoint[][] {
  const segs: SamplePoint[][] = [];
  let cur: SamplePoint[] = [];
  const jumpThreshold = yRange * 5; // y change > 5x viewport range = asymptote break
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (!p.defined) {
      if (cur.length) segs.push(cur);
      cur = [];
      continue;
    }
    if (cur.length) {
      const last = cur[cur.length - 1];
      if (Math.abs(p.y - last.y) > jumpThreshold) {
        segs.push(cur);
        cur = [];
      }
    }
    cur.push(p);
  }
  if (cur.length) segs.push(cur);
  return segs;
}

/** Find approximate zeros via sign change + linear interpolation. */
export function findZeros(points: SamplePoint[], maxResults = 12): number[] {
  const zeros: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a.defined || !b.defined) continue;
    if (a.y === 0) zeros.push(a.x);
    else if (a.y * b.y < 0) {
      // linear interp
      const t = a.y / (a.y - b.y);
      zeros.push(a.x + t * (b.x - a.x));
    }
    if (zeros.length >= maxResults) break;
  }
  return zeros;
}

/** Find approximate local extrema via discrete derivative sign change. */
export function findExtrema(
  points: SamplePoint[],
  maxResults = 8,
): { x: number; y: number; kind: "min" | "max" }[] {
  const out: { x: number; y: number; kind: "min" | "max" }[] = [];
  for (let i = 2; i < points.length - 1; i++) {
    const a = points[i - 1];
    const b = points[i];
    const c = points[i + 1];
    if (!a.defined || !b.defined || !c.defined) continue;
    const d1 = b.y - a.y;
    const d2 = c.y - b.y;
    if (d1 > 0 && d2 < 0) out.push({ x: b.x, y: b.y, kind: "max" });
    else if (d1 < 0 && d2 > 0) out.push({ x: b.x, y: b.y, kind: "min" });
    if (out.length >= maxResults) break;
  }
  return out;
}

/** Find intersections between two sampled curves (assumes same x grid). */
export function findIntersections(
  a: SamplePoint[],
  b: SamplePoint[],
  maxResults = 10,
): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  const n = Math.min(a.length, b.length);
  for (let i = 1; i < n; i++) {
    const a0 = a[i - 1], a1 = a[i], b0 = b[i - 1], b1 = b[i];
    if (!a0.defined || !a1.defined || !b0.defined || !b1.defined) continue;
    const d0 = a0.y - b0.y;
    const d1 = a1.y - b1.y;
    if (d0 === 0) {
      out.push({ x: a0.x, y: a0.y });
    } else if (d0 * d1 < 0) {
      const t = d0 / (d0 - d1);
      const x = a0.x + t * (a1.x - a0.x);
      const y = a0.y + t * (a1.y - a0.y);
      out.push({ x, y });
    }
    if (out.length >= maxResults) break;
  }
  return out;
}

export function specialPoints(fn: CompiledFn, points: SamplePoint[], trigMode: "rad" | "deg" = "rad"): SpecialPoints {
  const yIntercept = (() => {
    const xEval = trigMode === "deg" ? 0 : 0;
    const v = fn.evaluate(xEval);
    return Number.isFinite(v) ? v : null;
  })();
  return {
    zeros: findZeros(points),
    yIntercept,
    extrema: findExtrema(points),
  };
}

/** Auto-fit Y range from sampled curves with a small margin, clipped to a sane band. */
export function autoYRange(allPoints: SamplePoint[][], xRange: number): { yMin: number; yMax: number } {
  let lo = Infinity, hi = -Infinity;
  for (const series of allPoints) {
    for (const p of series) {
      if (!p.defined) continue;
      if (p.y < lo) lo = p.y;
      if (p.y > hi) hi = p.y;
    }
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    return { yMin: -10, yMax: 10 };
  }
  if (lo === hi) {
    return { yMin: lo - 1, yMax: hi + 1 };
  }
  // Clip wild ranges
  const span = hi - lo;
  const cap = Math.max(xRange * 2, 20);
  if (span > cap) {
    const mid = (lo + hi) / 2;
    lo = mid - cap / 2;
    hi = mid + cap / 2;
  }
  const pad = (hi - lo) * 0.1;
  return { yMin: lo - pad, yMax: hi + pad };
}

export const PRESETS: { label: string; expr: string }[] = [
  { label: "y = x²", expr: "x^2" },
  { label: "y = x³", expr: "x^3" },
  { label: "y = sin(x)", expr: "sin(x)" },
  { label: "y = cos(x)", expr: "cos(x)" },
  { label: "y = tan(x)", expr: "tan(x)" },
  { label: "y = log(x)", expr: "log10(x)" },
  { label: "y = ln(x)", expr: "log(x)" },
  { label: "y = e^x", expr: "exp(x)" },
  { label: "y = 1/x", expr: "1/x" },
  { label: "y = √x", expr: "sqrt(x)" },
  { label: "y = |x|", expr: "abs(x)" },
  { label: "y = a·x² + b·x + c", expr: "a*x^2 + b*x + c" },
];

export const DEFAULT_COLORS = [
  "#2563eb", // blue
  "#dc2626", // red
  "#16a34a", // green
  "#ea580c", // orange
  "#9333ea", // purple
  "#0891b2", // teal
];
